import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { deleteImagesFromCloudinary } from './cloudinary';
import { mergeFeaturedFarmers } from '../data/farmers';

let resolvedHeaderTickerCache = null;
const HEADER_TICKER_PUBLIC_DOC = ['terms', 'headerTickerPublic'];
const HEADER_TICKER_ADMIN_DOC = ['siteSettings', 'headerTicker'];

function buildDefaultHeaderTickerConfig() {
  return {
    enabled: true,
    operatorPostIds: [],
    farmerReportIds: [],
    operatorDisplayCount: 0,
    farmerDisplayCount: 0,
    operatorSelectionMode: 'fixed',
    farmerSelectionMode: 'random',
    priority: 'operator',
  };
}

function shuffle(items) {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function pickTickerItems(items, selectedIds, count, mode, mapFn) {
  const selected = selectedIds
    .map((id) => items.find((row) => row.id === id))
    .filter(Boolean)
    .map(mapFn);

  if (!selected.length || count <= 0) return [];
  const base = mode === 'random' ? shuffle(selected) : selected;
  return base.slice(0, count);
}

function sortAnnouncementItems(items) {
  return [...items].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function mapOperatorAnnouncement(post) {
  return {
    id: `post-${post.id}`,
    type: 'operator',
    sourceId: post.id,
    to: `/posts/${post.slug}`,
    title: post.title || '',
    date: post.publishedDate || '',
    text: `${post.title}${post.body ? ` | ${post.body}` : ''}`,
    summary: post.body || '',
  };
}

function mapFarmerAnnouncement(report) {
  return {
    id: `report-${report.id}`,
    type: 'farmer',
    sourceId: report.id,
    to: report.farmerSlug ? `/farmers/${report.farmerSlug}#monthly-report-${report.id}` : '/farmers',
    title: report.title || report.highlights || '',
    date: report.publishedDate || report.month || '',
    text: `${report.farmerName || '農家'} | ${report.title || report.highlights || '近況報告'}`,
    summary: report.body || '',
    farmerName: report.farmerName || '農家',
  };
}

function buildAnnouncementFeed(posts, reports) {
  return sortAnnouncementItems([
    ...posts.map(mapOperatorAnnouncement),
    ...reports.map(mapFarmerAnnouncement),
  ]);
}

export async function listPublicOperatorAnnouncements(limitCount = 6) {
  const posts = await listPublicPosts();
  const items = sortAnnouncementItems(posts.map(mapOperatorAnnouncement));
  return items.slice(0, Math.max(0, Number(limitCount) || 0));
}

export async function listFarmers(filters = {}) {
  const constraints = [];
  if (filters.prefecture) constraints.push(where('prefecture', '==', filters.prefecture));
  if (filters.farmerType) constraints.push(where('farmerType', '==', filters.farmerType));

  const q = query(collection(db, 'farmers'), ...constraints);
  let snap;
  try {
    snap = await getDocsFromServer(q);
  } catch {
    snap = await getDocs(q);
  }
  const rows = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  if (!filters.crop) return rows;
  return rows.filter((item) => (item.crops || []).includes(filters.crop));
}

export async function getFarmerBySlug(slug) {
  const q = query(collection(db, 'farmers'), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  const found = snap.docs[0];
  return found ? { id: found.id, ...found.data() } : null;
}

export async function listFarmerSoilTests(farmerId) {
  const q = query(
    collection(db, 'farmers', farmerId, 'soilTests'),
    orderBy('date', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listFarmerMonthlyReports(farmerId, includePrivate = false) {
  const constraints = [orderBy('month', 'desc')];
  if (!includePrivate) {
    constraints.unshift(where('visibility', '==', 'public'));
  }
  const q = query(collection(db, 'farmers', farmerId, 'monthlyReports'), ...constraints);
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (includePrivate) return rows;
  return rows.filter((row) => row.approvalStatus === 'approved' || !row.approvalStatus);
}

export async function listPublicPosts() {
  const q = query(
    collection(db, 'posts'),
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return rows.filter((row) => row.approvalStatus === 'approved' || !row.approvalStatus);
}

export async function listPublicFarmerReports() {
  try {
    const [farmers, snap] = await Promise.all([
      Promise.resolve(mergeFeaturedFarmers(await listFarmers())),
      getDocs(query(
        collectionGroup(db, 'monthlyReports'),
        where('visibility', '==', 'public')
      )),
    ]);

    const farmerMap = new Map(farmers.map((row) => [row.id, row]));
    const rows = snap.docs.map((d) => {
      const parts = d.ref.path.split('/');
      const farmerId = parts[1];
      const farmer = farmerMap.get(farmerId);
      return {
        id: d.id,
        farmerId,
        farmerName: farmer?.name || '',
        farmerSlug: farmer?.slug || '',
        ...d.data(),
      };
    });

    return rows
      .filter((row) => row.approvalStatus === 'approved' || !row.approvalStatus)
      .sort((a, b) => {
        const aDate = String(a.publishedDate || a.month || '');
        const bDate = String(b.publishedDate || b.month || '');
        return bDate.localeCompare(aDate);
      });
  } catch (error) {
    if (error?.code !== 'failed-precondition' && error?.code !== 'permission-denied') {
      throw error;
    }

    const farmers = mergeFeaturedFarmers(await listFarmers());
    const reportGroups = await Promise.all(
      farmers.map(async (farmer) => {
        try {
          const reports = await listFarmerMonthlyReports(farmer.id, false);
          return reports.map((report) => ({
            ...report,
            farmerId: farmer.id,
            farmerName: farmer.name || '',
            farmerSlug: farmer.slug || '',
          }));
        } catch (nestedError) {
          if (nestedError?.code === 'permission-denied') {
            return [];
          }
          throw nestedError;
        }
      })
    );

    return reportGroups
      .flat()
      .sort((a, b) => {
        const aDate = String(a.publishedDate || a.month || '');
        const bDate = String(b.publishedDate || b.month || '');
        return bDate.localeCompare(aDate);
      });
  }
}

async function listFarmerReportsByApprovalStatus(approvalStatus) {
  const farmers = await listFarmers();
  const reportGroups = await Promise.all(
    farmers.map(async (farmer) => {
      const snap = await getDocs(query(
        collection(db, 'farmers', farmer.id, 'monthlyReports'),
        where('approvalStatus', '==', approvalStatus)
      ));
      return snap.docs.map((d) => ({
        id: d.id,
        farmerId: farmer.id,
        farmerName: farmer.name || '',
        farmerSlug: farmer.slug || '',
        ...d.data(),
      }));
    })
  );

  return reportGroups
    .flat()
    .sort((a, b) => {
      const aDate = String(a.publishedDate || a.month || '');
      const bDate = String(b.publishedDate || b.month || '');
      return bDate.localeCompare(aDate);
    });
}

export async function listPostsForAdmin() {
  const snap = await getDocs(collection(db, 'posts'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a?.createdAt?.seconds || 0;
      const bTime = b?.createdAt?.seconds || 0;
      return bTime - aTime;
    });
}

export async function listPublicDoctors() {
  const snap = await getDocs(collection(db, 'doctors'));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return rows
    .filter((row) => row.visibility !== 'private')
    .sort((a, b) => (Number(a.displayOrder ?? 9999) - Number(b.displayOrder ?? 9999)) || String(a.name || '').localeCompare(String(b.name || '')));
}

export async function getPostBySlug(slug) {
  const q = query(
    collection(db, 'posts'),
    where('slug', '==', slug),
    where('visibility', '==', 'public'),
    limit(1)
  );
  const snap = await getDocs(q);
  const found = snap.docs[0];
  if (!found) return null;
  const row = { id: found.id, ...found.data() };
  return (row.approvalStatus === 'approved' || !row.approvalStatus) ? row : null;
}

export async function listPublicPostsByFarmerTag(farmerId) {
  const posts = await listPublicPosts();
  return posts.filter((post) => (post.farmerTagIds || []).includes(farmerId));
}

export async function listPendingPosts() {
  const q = query(
    collection(db, 'posts'),
    where('approvalStatus', '==', 'pending')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listPendingFarmerReports() {
  try {
    const q = query(
      collectionGroup(db, 'monthlyReports'),
      where('approvalStatus', '==', 'pending')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const parts = d.ref.path.split('/');
      return {
        id: d.id,
        farmerId: parts[1],
        ...d.data(),
      };
    });
  } catch (error) {
    if (error?.code !== 'failed-precondition') {
      throw error;
    }
    const snap = await getDocs(collectionGroup(db, 'monthlyReports'));
    return snap.docs
      .map((d) => {
        const parts = d.ref.path.split('/');
        return {
          id: d.id,
          farmerId: parts[1],
          ...d.data(),
        };
      })
      .filter((row) => row.approvalStatus === 'pending');
  }
}

export async function listAllFarmerReportsForReviewer() {
  const snap = await getDocs(collectionGroup(db, 'monthlyReports'));
  return snap.docs.map((d) => {
    const parts = d.ref.path.split('/');
    return {
      id: d.id,
      farmerId: parts[1],
      ...d.data(),
    };
  });
}

export async function listInstagramRefs(limitCount = 6) {
  const q = query(
    collection(db, 'instagramRefs'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getHeaderTickerConfig() {
  try {
    const publicSnap = await getDoc(doc(db, ...HEADER_TICKER_PUBLIC_DOC));
    if (publicSnap.exists()) {
      return publicSnap.data();
    }
  } catch (error) {
    if (error?.code && error.code !== 'permission-denied') {
      throw error;
    }
  }

  try {
    const snap = await getDoc(doc(db, ...HEADER_TICKER_ADMIN_DOC));
    return snap.exists()
      ? snap.data()
      : buildDefaultHeaderTickerConfig();
  } catch (error) {
    if (error?.code !== 'permission-denied') {
      throw error;
    }
    return buildDefaultHeaderTickerConfig();
  }
}

export async function getResolvedHeaderTickerData(options = {}) {
  const force = Boolean(options?.force);
  const now = Date.now();
  if (!force && resolvedHeaderTickerCache && resolvedHeaderTickerCache.expiresAt > now) {
    return resolvedHeaderTickerCache.data;
  }

  const [config, posts, reports] = await Promise.all([
    getHeaderTickerConfig(),
    listPublicPosts(),
    listPublicFarmerReports(),
  ]);
  const allItems = buildAnnouncementFeed(posts, reports);

  const enabled = config?.enabled ?? true;
  if (!enabled) {
    const disabled = {
      enabled,
      items: [],
      allItems,
      allPosts: posts,
      allReports: reports,
    };
    resolvedHeaderTickerCache = {
      expiresAt: now + 30_000,
      data: disabled,
    };
    return disabled;
  }

  const operatorItems = pickTickerItems(
    posts,
    config?.operatorPostIds || [],
    Number(config?.operatorDisplayCount) || 0,
    config?.operatorSelectionMode || 'fixed',
    mapOperatorAnnouncement
  );
  const farmerItems = pickTickerItems(
    reports,
    config?.farmerReportIds || [],
    Number(config?.farmerDisplayCount) || 0,
    config?.farmerSelectionMode || 'random',
    mapFarmerAnnouncement
  );

  const configuredItems = config?.priority === 'farmer'
    ? [...farmerItems, ...operatorItems]
    : [...operatorItems, ...farmerItems];
  const items = configuredItems.length ? configuredItems : allItems.slice(0, 3);

  const data = {
    enabled,
    items,
    allItems,
    allPosts: posts,
    allReports: reports,
  };
  resolvedHeaderTickerCache = {
    expiresAt: now + 30_000,
    data,
  };
  return data;
}

export async function setHeaderTickerConfig(payload, uid) {
  const normalized = {
    ...payload,
    updatedBy: uid,
    updatedAt: serverTimestamp(),
  };

  const [result] = await Promise.all([
    setDoc(doc(db, ...HEADER_TICKER_ADMIN_DOC), normalized, { merge: true }),
    setDoc(doc(db, ...HEADER_TICKER_PUBLIC_DOC), normalized, { merge: true }),
  ]);
  resolvedHeaderTickerCache = null;
  return result;
}

export async function getApprovalNotifierSettings() {
  const snap = await getDoc(doc(db, 'siteSettings', 'approvalNotifier'));
  if (!snap.exists()) {
    return {
      enabled: true,
      notifyAdminUids: [],
      notifyEmails: [],
    };
  }
  const row = snap.data() || {};
  return {
    enabled: row.enabled !== false,
    notifyAdminUids: Array.isArray(row.notifyAdminUids) ? row.notifyAdminUids.filter(Boolean) : [],
    notifyEmails: Array.isArray(row.notifyEmails) ? row.notifyEmails.filter(Boolean) : [],
    updatedAt: row.updatedAt || null,
    updatedBy: row.updatedBy || '',
  };
}

export async function setApprovalNotifierSettings(payload, uid) {
  return setDoc(doc(db, 'siteSettings', 'approvalNotifier'), {
    enabled: payload?.enabled !== false,
    notifyAdminUids: Array.isArray(payload?.notifyAdminUids) ? payload.notifyAdminUids.filter(Boolean) : [],
    notifyEmails: Array.isArray(payload?.notifyEmails) ? payload.notifyEmails.filter(Boolean) : [],
    updatedBy: uid,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function isAdminUser(uid) {
  if (!uid) return false;
  const adminDoc = await getDoc(doc(db, 'rolesAdmins', uid));
  return adminDoc.exists() && adminDoc.data()?.role === 'site_admin';
}

export async function getOperatorRole(uid) {
  if (!uid) return null;
  const roleDoc = await getDoc(doc(db, 'rolesAdmins', uid));
  const role = roleDoc.exists() ? roleDoc.data()?.role : null;
  if (!role) return null;
  return role === 'staff' ? 'staff' : 'site_admin';
}

export async function getFarmerByOwnerUid(uid) {
  if (!uid) return null;
  const q = query(collection(db, 'farmers'), where('ownerUid', '==', uid), limit(1));
  const snap = await getDocs(q);
  const found = snap.docs[0];
  return found ? { id: found.id, ...found.data() } : null;
}

export async function setOperatorRole(uid, role, updatedBy) {
  const roleValue = role === 'staff' ? 'staff' : 'site_admin';
  return setDoc(doc(db, 'rolesAdmins', uid), {
    role: roleValue,
    updatedBy,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function upsertUserProfile(uid, payload) {
  return setDoc(doc(db, 'users', uid), {
    ...payload,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

export async function listUserProfiles({ role } = {}) {
  const constraints = [];
  if (role) constraints.push(where('role', '==', role));
  const q = query(collection(db, 'users'), ...constraints);
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ uid: d.id, ...d.data() }))
    .sort((a, b) => String(a.email || '').localeCompare(String(b.email || '')));
}

export async function logPasswordView({
  actorUid,
  actorEmail = '',
  targetUid,
  targetEmail = '',
  targetRole = '',
  scope = '',
}) {
  const now = new Date();
  const expireAt = new Date(now.getTime() + 183 * 24 * 60 * 60 * 1000);
  await addDoc(collection(db, 'passwordViewLogs'), {
    actorUid,
    actorEmail,
    targetUid,
    targetEmail,
    targetRole,
    scope,
    viewedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    expireAt,
  });
}

export async function cleanupExpiredPasswordViewLogs() {
  const q = query(collection(db, 'passwordViewLogs'), where('expireAt', '<', new Date()));
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map((d) => deleteDoc(doc(db, 'passwordViewLogs', d.id))));
}

export async function listPasswordViewLogs(limitCount = 100) {
  const q = query(
    collection(db, 'passwordViewLogs'),
    orderBy('viewedAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createFarmer(payload, uid) {
  return addDoc(collection(db, 'farmers'), {
    ...payload,
    ownerUid: payload.ownerUid || uid,
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function updateFarmer(farmerId, payload) {
  const ref = doc(db, 'farmers', farmerId);
  return updateDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function createPost(payload, uid) {
  return addDoc(collection(db, 'posts'), {
    ...payload,
    approvalStatus: payload.approvalStatus || 'draft',
    visibility: payload.visibility || 'private',
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function createMonthlyReport(farmerId, payload, uid) {
  return addDoc(collection(db, 'farmers', farmerId, 'monthlyReports'), {
    ...payload,
    approvalStatus: payload.approvalStatus || 'draft',
    visibility: payload.visibility || 'private',
    farmerTagIds: payload.farmerTagIds || [farmerId],
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function createSoilTest(farmerId, payload, uid) {
  return addDoc(collection(db, 'farmers', farmerId, 'soilTests'), {
    ...payload,
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function listDoctors() {
  const q = query(collection(db, 'doctors'), orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createDoctor(payload, uid) {
  return addDoc(collection(db, 'doctors'), {
    ...payload,
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function updateDoctor(doctorId, payload) {
  return updateDoc(doc(db, 'doctors', doctorId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteDoctor(doctorId) {
  return deleteDoc(doc(db, 'doctors', doctorId));
}

export async function updatePost(postId, payload) {
  return updateDoc(doc(db, 'posts', postId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function submitPostForReview(postId, uid) {
  await updateDoc(doc(db, 'posts', postId), {
    approvalStatus: 'pending',
    submittedForReviewAt: serverTimestamp(),
    submittedForReviewBy: uid,
    visibility: 'private',
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'submitted',
      targetType: 'post',
      targetId: postId,
      actorUid: uid,
      approvalStatus: 'pending',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (post submit)', error);
  }
}

export async function approvePost(postId, reviewerUid) {
  const ref = doc(db, 'posts', postId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('post-not-found');
  const row = snap.data();
  if (row.createdBy === reviewerUid) {
    throw new Error('self-approval-not-allowed');
  }
  const removedImageIds = Array.isArray(row.pendingRemovedImagePublicIds) ? row.pendingRemovedImagePublicIds : [];
  if (removedImageIds.length) {
    await deleteImagesFromCloudinary(removedImageIds, reviewerUid);
  }
  await updateDoc(ref, {
    approvalStatus: 'approved',
    approvedBy: reviewerUid,
    approvedAt: serverTimestamp(),
    visibility: 'public',
    pendingRemovedImagePublicIds: [],
    pendingChangeSummary: [],
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'approved',
      targetType: 'post',
      targetId: postId,
      actorUid: reviewerUid,
      approvalStatus: 'approved',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (post approve)', error);
  }
}

export async function rejectPost(postId, reviewerUid, note = '') {
  const ref = doc(db, 'posts', postId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('post-not-found');
  const row = snap.data();
  if (row.createdBy === reviewerUid) {
    throw new Error('self-approval-not-allowed');
  }
  await updateDoc(ref, {
    approvalStatus: 'rejected',
    rejectedBy: reviewerUid,
    rejectedAt: serverTimestamp(),
    reviewNote: note,
    visibility: 'private',
    pendingRemovedImagePublicIds: [],
    pendingChangeSummary: [],
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'rejected',
      targetType: 'post',
      targetId: postId,
      actorUid: reviewerUid,
      approvalStatus: 'rejected',
      note: note || '',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (post reject)', error);
  }
}

export async function updateMonthlyReport(farmerId, reportId, payload) {
  return updateDoc(doc(db, 'farmers', farmerId, 'monthlyReports', reportId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function submitMonthlyReportForReview(farmerId, reportId, uid) {
  await updateDoc(doc(db, 'farmers', farmerId, 'monthlyReports', reportId), {
    approvalStatus: 'pending',
    submittedForReviewAt: serverTimestamp(),
    submittedForReviewBy: uid,
    visibility: 'private',
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'submitted',
      targetType: 'farmer_report',
      targetId: reportId,
      farmerId,
      actorUid: uid,
      approvalStatus: 'pending',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (report submit)', error);
  }
}

export async function approveMonthlyReport(farmerId, reportId, reviewerUid) {
  const ref = doc(db, 'farmers', farmerId, 'monthlyReports', reportId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('report-not-found');
  const row = snap.data();
  if (row.createdBy === reviewerUid) {
    throw new Error('self-approval-not-allowed');
  }
  const removedImageIds = Array.isArray(row.pendingRemovedImagePublicIds) ? row.pendingRemovedImagePublicIds : [];
  if (removedImageIds.length) {
    await deleteImagesFromCloudinary(removedImageIds, reviewerUid);
  }
  await updateDoc(ref, {
    approvalStatus: 'approved',
    approvedBy: reviewerUid,
    approvedAt: serverTimestamp(),
    visibility: 'public',
    pendingRemovedImagePublicIds: [],
    pendingChangeSummary: [],
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'approved',
      targetType: 'farmer_report',
      targetId: reportId,
      farmerId,
      actorUid: reviewerUid,
      approvalStatus: 'approved',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (report approve)', error);
  }
}

export async function rejectMonthlyReport(farmerId, reportId, reviewerUid, note = '') {
  const ref = doc(db, 'farmers', farmerId, 'monthlyReports', reportId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('report-not-found');
  const row = snap.data();
  if (row.createdBy === reviewerUid) {
    throw new Error('self-approval-not-allowed');
  }
  await updateDoc(ref, {
    approvalStatus: 'rejected',
    rejectedBy: reviewerUid,
    rejectedAt: serverTimestamp(),
    reviewNote: note,
    visibility: 'private',
    pendingRemovedImagePublicIds: [],
    pendingChangeSummary: [],
    updatedAt: serverTimestamp(),
  });
  try {
    await addDoc(collection(db, 'approvalEvents'), {
      eventType: 'rejected',
      targetType: 'farmer_report',
      targetId: reportId,
      farmerId,
      actorUid: reviewerUid,
      approvalStatus: 'rejected',
      note: note || '',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('approval event log failed (report reject)', error);
  }
}

export async function updateSoilTest(farmerId, testId, payload) {
  return updateDoc(doc(db, 'farmers', farmerId, 'soilTests', testId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteFarmer(farmerId) {
  return deleteDoc(doc(db, 'farmers', farmerId));
}

export async function deletePost(postId) {
  return deleteDoc(doc(db, 'posts', postId));
}

export async function deleteMonthlyReport(farmerId, reportId) {
  return deleteDoc(doc(db, 'farmers', farmerId, 'monthlyReports', reportId));
}

export async function deleteSoilTest(farmerId, testId) {
  return deleteDoc(doc(db, 'farmers', farmerId, 'soilTests', testId));
}
